import { AlertCircle } from "lucide-react";
import { Navigate } from "react-router-dom";

import { useIsSuperAdmin } from "@/api/is-superadmin";
import { Card } from "@/components/card";
import { LoadingScreen } from "@/components/loading-screen";
import { useTableControl } from "@/components/table-control";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { TabNavigation } from "@/components/ui/tab-navigation";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useGetOrganisation } from "@/features/organisations/api/get-organisation";
import { useRecordings } from "@/features/recordings/api/fetch-recordings";
import { RecordingStorageTable } from "@/features/recordings/components/recording-storage-table";
import { OnboardingHeader } from "@/features/registration/components/onboarding-header";
import { useOrganisation } from "@/features/select-organisation/api/get-my-organisation";
import { cn } from "@/lib/utils";

function bytesToGi(bytes: number): number {
  return Number((bytes / (1024 * 1024 * 1024)).toFixed(2));
}

export function RecordingsStoragePage() {
  const { isSuperAdmin, isLoading } = useIsSuperAdmin();
  const { organisation } = useOrganisation();
  const { data: organisationDetails } = useGetOrganisation({
    id: organisation?.id ?? "",
  });
  const { pagination, setPagination } = useTableControl({
    initialPageSize: 10,
  });
  const {
    error: recordingsError,
    isLoading: isRecordingsLoading,
    isFetching: isRecordingsFetching,
    isError: isRecordingsError,
    isSuccess: isRecordingsSuccess,
    data: recordings,
  } = useRecordings({
    pageSize: pagination.pageSize,
    pageIndex: pagination.pageIndex,
    organisationId: organisation?.id ?? "",
  });

  if (!isSuperAdmin) {
    return <Navigate to="/recordings" />;
  }

  if (isLoading) {
    return <LoadingScreen />;
  }

  const navigationItems = [
    { label: "All Recordings", to: "/recordings/all" },
    { label: "Playlists", to: "/recordings/playlists" },
    ...(isSuperAdmin
      ? [
          {
            label: "Storage",
            to: "/recordings/storage",
          },
        ]
      : []),
  ];

  const usedStorageGB = bytesToGi(
    organisationDetails?.totalStorageUsedBytes ?? 0,
  );
  const totalUsedStoragePercentage =
    (usedStorageGB / (organisationDetails?.license.storageInGB ?? 0)) * 100;

  const permanentStorageGB = bytesToGi(
    organisationDetails?.permanentStorageUsedBytes ?? 0,
  );
  const permanentStoragePercentage = (permanentStorageGB / usedStorageGB) * 100;

  const temporaryStorageGB = usedStorageGB - permanentStorageGB;
  const temporaryStoragePercentage = (temporaryStorageGB / usedStorageGB) * 100;

  const isPermanentStorageSmaller = permanentStorageGB < temporaryStorageGB;

  const isTableLoading = isRecordingsFetching || isRecordingsLoading;

  const recordingCount = recordings?.count ?? 0;
  const pageCount = Math.ceil(recordingCount / pagination.pageSize);

  return (
    <section>
      <OnboardingHeader
        title="Content Library"
        description="View your organisations recordings"
        className="mb-6"
      />
      <TabNavigation className="mb-6" options={navigationItems} />
      <div className="flex flex-col gap-4">
        {organisation && (
          <>
            <TooltipProvider>
              <Card className="w-full">
                <Card.Header title="Storage Overview" />
                <Card.Content className="flex flex-col gap-4">
                  <div className="relative h-2 w-full rounded-md bg-card">
                    <div
                      className={cn(
                        "flex h-full w-full flex-row overflow-hidden rounded-md transition-all",
                        isPermanentStorageSmaller && "z-10",
                      )}
                      style={{ width: `${totalUsedStoragePercentage}%` }}
                    >
                      <Tooltip delayDuration={0} key="permanent-storage">
                        <TooltipTrigger asChild>
                          <div
                            className="left-0 top-0 h-full bg-primary transition-all"
                            style={{ width: `${permanentStoragePercentage}%` }}
                          />
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>
                            {permanentStorageGB.toFixed(2)} GB Permanent storage
                          </p>
                        </TooltipContent>
                      </Tooltip>
                      <Tooltip delayDuration={0} key="temporary-storage">
                        <TooltipTrigger asChild>
                          <div
                            className="left-0 top-0 h-full bg-warning transition-all"
                            style={{ width: `${temporaryStoragePercentage}%` }}
                          />
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>
                            {temporaryStorageGB.toFixed(2)} GB Temporary storage
                          </p>
                        </TooltipContent>
                      </Tooltip>
                    </div>
                  </div>
                  <div className="flex flex-col gap-2 text-sm text-muted-foreground">
                    {usedStorageGB.toFixed(2)} /{" "}
                    {organisationDetails?.license.storageInGB} GB used
                  </div>
                </Card.Content>
              </Card>
            </TooltipProvider>
            {isRecordingsSuccess && (
              <RecordingStorageTable
                data={recordings?.items ?? []}
                pagination={pagination}
                onPaginationChange={setPagination}
                pageCount={pageCount}
                isLoading={isTableLoading}
              />
            )}

            {isRecordingsError && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertTitle>Error</AlertTitle>
                <AlertDescription>
                  {recordingsError?.message ?? "An unexpected error occurred"}
                </AlertDescription>
              </Alert>
            )}
          </>
        )}
      </div>
    </section>
  );
}
